import { textGray600 } from '@osmosis/tailwind-ui/styles/text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Checkbox } from '@osmosis/tailwind-ui/components/Checkbox';
import { Dispatch, SetStateAction, ChangeEvent } from 'react';
import { PopupMenuItem } from '../../../components/PopupMenu';
import {
  PlaylistLearnNodeItem,
  PlaylistType,
} from '@osmosis/shared/types/playlist';
import { useSnackbarContext } from '../../../context/SnackbarProvider';
import { usePlaylistContext } from '../../../context/PlaylistProvider';
import { ModalHookProps } from '../../../hooks/useModal';

type Props<T> = {
  item: T;
  checkboxState: [
    { [url: string]: boolean }[],
    Dispatch<SetStateAction<{ [url: string]: boolean }[]>>,
  ];

  hasPrime: boolean;
  nonPrimeLockoutModal: ModalHookProps;
  closeAllMenus: () => void;
};

/**
 * PopupMenuRowWithCheckbox.
 */
const PopupMenuRowWithCheckbox = <
  T extends PopupMenuItem & {
    items: PlaylistLearnNodeItem[];
    playlistName: string;
    playlistUrl: string;
    type: PlaylistType;
  },
>(
  props: Props<T>,
) => {
  const { item, checkboxState, hasPrime, nonPrimeLockoutModal, closeAllMenus } =
    props;

  const { copyPlaylistVideos, removePlaylistVideos, createPlaylist } =
    usePlaylistContext();
  const { onAddSnackBar } = useSnackbarContext();

  const [checked, setChecked] = checkboxState;

  const isCurrentChecked = !!checked.find((el) => el[item.id] === true);

  const showPurchasePrimeModal = () => {
    closeAllMenus();
    nonPrimeLockoutModal.show();
  };

  const onChecked = async () => {
    if (hasPrime) {
      if (item.text === 'Watch later' && !item.id) {
        const { url, title } = await createPlaylist({
          title: item.text,
          onAddSnackBar,
        });
        if (title && url) {
          item.playlistName = title;
          item.playlistUrl = url;
        }
      }

      return await copyPlaylistVideos({
        items: item.items,
        anotherPlaylistName: item.playlistName,
        anotherPlaylistUrl: item.playlistUrl,
        type: item.type,
        onAddSnackBar,
      });
    } else {
      showPurchasePrimeModal();
    }
  };

  const onUnChecked = async () => {
    if (hasPrime) {
      return await removePlaylistVideos({
        items: item.items,
        playlistName: item.playlistName,
        playlistUrl: item.playlistUrl,
        onAddSnackBar,
      });
    } else {
      showPurchasePrimeModal();
    }
  };

  const handleChecked = (state: boolean) => {
    (state ? onChecked() : onUnChecked()).then(() => {
      if (hasPrime) {
        const filteredChecked = checked.filter((obj) => !(item.id in obj));
        filteredChecked.push({ [item.id]: state });
        setChecked(filteredChecked);
      }
    });
  };

  return (
    <Button
      variant="transparent"
      data-testid="manage-playlist-videos-btn"
      className={`${textGray600} w-[100%] flex justify-start`}
    >
      <Flex>
        {item.icon && (
          <Box className="basis-[22px]">
            <Icon icon={item.icon} />
          </Box>
        )}
        <Checkbox
          label={<P className={`w-[100%]`}>{item.text}</P>}
          checked={isCurrentChecked}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            handleChecked(!isCurrentChecked);
          }}
          data-testid="manage-playlist-videos-checkbox"
        />
      </Flex>
    </Button>
  );
};

export default PopupMenuRowWithCheckbox;
